// ============================================================================
// SessionLog — the SESSION LOG screen (route /app/sessions/:id).
//   • Header: client, status, when, type, location, fee/paid — tap to edit.
//   • Summary: total volume + est. calories from the session detail, plus set
//     and cardio counts (the engine totals, not re-derived).
//   • Strength sets: one card per set with reps/weight/RPE/RIR/side + volume.
//   • Cardio: one card per cardio entry with its metrics.
//   • Apply-template: drop a saved workout in.
// Data: useSession (live-synced). Adds go through SetEditor / CardioEditor.
// Reduced-motion-aware throughout; mobile-first with a sticky add bar.
// ============================================================================
import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { AttendanceControls, Badge, Button, Card, EmptyState } from "../../components";
import { IconClock, IconMapPin, IconPlus } from "../../components/icons";
import { useSession } from "../../hooks/sessions";
import { useClient } from "../../hooks/clients";
import { useExercises } from "../../hooks/exercises";
import type { AttendanceStatus, CardioEntry, Exercise, SetEntry } from "../../api";
import { LoadingBlock, ErrorBlock } from "../../app/PageState";
import { SessionForm } from "./SessionForm";
import { SetEditor } from "./SetEditor";
import { CardioEditor } from "./CardioEditor";
import { TemplateApply } from "./TemplateApply";
import {
  cardioSummary,
  exerciseName,
  fmtDateTime,
  fmtNum,
  setVolume,
} from "./lib";
import { formatMoney } from "../../lib/currency";
import { cn } from "../../lib/cn";

const EASE = [0.22, 0.61, 0.36, 1] as const;

export function SessionLog({ sessionId }: { sessionId: number }) {
  const { t, i18n } = useTranslation("sessions");
  const reduce = useReducedMotion();
  const navigate = useNavigate();

  const sessionQuery = useSession(sessionId);
  const detail = sessionQuery.data;
  const session = detail?.session;

  const clientQuery = useClient(session?.client_id);
  // One catalog fetch → id→exercise map (avoids an N+1 per set/cardio row).
  const exercisesQuery = useExercises({});
  const exMap = useMemo(() => {
    const map = new Map<number, Exercise>();
    for (const ex of exercisesQuery.data ?? []) map.set(ex.id, ex);
    return map;
  }, [exercisesQuery.data]);

  const [editOpen, setEditOpen] = useState(false);
  const [setOpen, setSetOpen] = useState(false);
  const [cardioOpen, setCardioOpen] = useState(false);

  if (sessionQuery.isLoading) {
    return <LoadingBlock label={t("loading")} />;
  }
  if (sessionQuery.isError) {
    return <ErrorBlock onRetry={() => void sessionQuery.refetch()} />;
  }
  if (!detail || !session) {
    return (
      <EmptyState
        title={t("log.notFound")}
        description={t("log.notFoundBlurb")}
        action={
          <Button onClick={() => navigate("/app/sessions")}>{t("back")}</Button>
        }
      />
    );
  }

  const isPlanned = session.status === "planned";
  const when = session.performed_at ?? session.scheduled_at;
  // Normalize the backend attendance_status (NULL → "scheduled") for the panel.
  const attendance: AttendanceStatus =
    session.attendance_status === "attended" ||
    session.attendance_status === "no_show" ||
    session.attendance_status === "cancelled"
      ? session.attendance_status
      : "scheduled";
  const sets = detail.sets;
  const cardio = detail.cardio;
  const nextSetNo = sets.reduce((m, s) => Math.max(m, s.set_no ?? 0), 0) + 1;

  const fade = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4, delay: reduce ? 0 : delay, ease: EASE },
  });

  const nameOf = (id: number | null) =>
    id == null ? "" : exerciseName(exMap.get(id), i18n.language) || `#${id}`;

  return (
    <div className="space-y-6 pb-24">
      {/* Back link */}
      <Link
        to="/app/sessions"
        className="inline-flex items-center gap-1.5 text-sm font-bold text-body-soft transition-colors hover:text-coral"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {t("back")}
      </Link>

      {/* Header card */}
      <motion.div {...fade(0)}>
        <Card tone="ink">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={isPlanned ? "volt" : "success"} solid dot>
                  {t(isPlanned ? "status.planned" : "status.completed")}
                </Badge>
                {clientQuery.data && (
                  <Link
                    to={`/app/clients/${session.client_id}`}
                    className="font-display text-sm font-bold text-ondark-muted underline-offset-2 hover:text-ondark hover:underline"
                  >
                    {clientQuery.data.name}
                  </Link>
                )}
              </div>
              <h1 className="mt-2 font-display text-2xl font-extrabold tracking-tightest text-ondark sm:text-3xl">
                {when ? fmtDateTime(when) : t("log.undated")}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ondark-muted">
                {session.type && (
                  <span className="font-bold">{session.type}</span>
                )}
                {session.duration_min != null && (
                  <span className="inline-flex items-center gap-1.5">
                    <IconClock width={15} height={15} />
                    {fmtNum(session.duration_min)} {t("form.minutes")}
                  </span>
                )}
                {session.location_text && (
                  <span className="inline-flex items-center gap-1.5">
                    <IconMapPin width={15} height={15} />
                    {session.location_text}
                  </span>
                )}
                {session.fee_amount != null && (
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 font-bold",
                      session.paid === 1 ? "text-lime" : "text-amber",
                    )}
                  >
                    {formatMoney(session.fee_amount, session.currency, {
                      locale: i18n.language,
                    })}
                    {" · "}
                    {t(session.paid === 1 ? "form.paid" : "form.notPaid")}
                  </span>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditOpen(true)}
              className="shrink-0 !bg-white/10 !text-ondark !ring-white/20 hover:!bg-white/20"
            >
              {t("log.edit")}
            </Button>
          </div>
        </Card>
      </motion.div>

      {/* Attendance / check-in — mark arrived (optional time), no-show, and the
          scannable check-in QR. Marking arrived sets the session attended +
          completed (the detail refetches on settle). */}
      <motion.div {...fade(0.03)}>
        <AttendanceControls
          sessionId={sessionId}
          attendance={attendance}
          checkedInAt={session.checked_in_at}
          clientName={clientQuery.data?.name}
        />
      </motion.div>

      {/* Summary tiles */}
      <motion.div
        {...fade(0.05)}
        className="grid grid-cols-2 gap-3 sm:grid-cols-4"
      >
        <SummaryTile
          label={t("log.totalVolume")}
          value={detail.total_volume}
          unit={t("log.volumeUnit")}
          accent="coral"
        />
        <SummaryTile
          label={t("log.estCalories")}
          value={detail.est_calories}
          unit={t("log.calUnit")}
          accent="amber"
        />
        <SummaryTile
          label={t("sets.title")}
          value={sets.length}
          unit={t("log.setCount", { count: sets.length })}
          accent="ocean"
          rawCount
        />
        <SummaryTile
          label={t("cardio.title")}
          value={cardio.length}
          unit={t("log.cardioCount", { count: cardio.length })}
          accent="lime"
          rawCount
        />
      </motion.div>

      {/* Apply template */}
      <motion.div {...fade(0.1)}>
        <TemplateApply sessionId={sessionId} />
      </motion.div>

      {/* Strength sets */}
      <motion.div {...fade(0.15)}>
        <SectionHeader
          title={t("sets.title")}
          subtitle={t("sets.subtitle")}
          onAdd={() => setSetOpen(true)}
          addLabel={t("sets.add")}
        />
        {sets.length === 0 ? (
          <button
            type="button"
            onClick={() => setSetOpen(true)}
            className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-line-strong bg-surface/60 py-8 font-display font-bold text-body-soft transition-colors hover:border-coral hover:text-coral"
          >
            <IconPlus width={18} height={18} />
            {t("sets.addFirst")}
          </button>
        ) : (
          <ul className="space-y-2">
            {sets.map((s, i) => (
              <SetCard key={s.id} set={s} name={nameOf(s.exercise_id)} index={i} />
            ))}
          </ul>
        )}
      </motion.div>

      {/* Cardio */}
      <motion.div {...fade(0.2)}>
        <SectionHeader
          title={t("cardio.title")}
          subtitle={t("cardio.subtitle")}
          onAdd={() => setCardioOpen(true)}
          addLabel={t("cardio.add")}
        />
        {cardio.length === 0 ? (
          <button
            type="button"
            onClick={() => setCardioOpen(true)}
            className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-line-strong bg-surface/60 py-8 font-display font-bold text-body-soft transition-colors hover:border-coral hover:text-coral"
          >
            <IconPlus width={18} height={18} />
            {t("cardio.addFirst")}
          </button>
        ) : (
          <ul className="space-y-2">
            {cardio.map((c, i) => (
              <CardioCard key={c.id} cardio={c} name={nameOf(c.exercise_id)} index={i} />
            ))}
          </ul>
        )}
      </motion.div>

      {/* Sticky mobile add bar */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur sm:hidden">
        <div className="mx-auto flex max-w-shell gap-2">
          <Button block onClick={() => setSetOpen(true)} iconLeft={<IconPlus width={18} height={18} />}>
            {t("sets.add")}
          </Button>
          <Button
            block
            variant="secondary"
            onClick={() => setCardioOpen(true)}
            iconLeft={<IconPlus width={18} height={18} />}
          >
            {t("cardio.add")}
          </Button>
        </div>
      </div>

      {/* Sheets */}
      <SessionForm
        open={editOpen}
        onClose={() => setEditOpen(false)}
        session={session}
      />
      <SetEditor
        open={setOpen}
        onClose={() => setSetOpen(false)}
        sessionId={sessionId}
        nextSetNo={nextSetNo}
      />
      <CardioEditor
        open={cardioOpen}
        onClose={() => setCardioOpen(false)}
        sessionId={sessionId}
      />
    </div>
  );
}

// --- summary tile -----------------------------------------------------------

function SummaryTile({
  label,
  value,
  unit,
  accent,
  rawCount,
}: {
  label: string;
  value: number | null;
  unit: string;
  accent: "coral" | "amber" | "ocean" | "lime";
  rawCount?: boolean;
}) {
  const accentEdge = {
    coral: "bg-grad-coral",
    amber: "bg-grad-amber",
    ocean: "bg-grad-ocean",
    lime: "bg-grad-lime",
  }[accent];

  const hasValue = value != null && (rawCount || value > 0);

  return (
    <div className="relative overflow-hidden rounded-lg bg-surface p-4 ring-1 ring-line shadow-sm">
      <span
        aria-hidden
        className={cn("absolute left-0 top-0 bottom-0 w-1.5 rounded-r-md", accentEdge)}
      />
      <p className="font-display text-[0.7rem] font-bold uppercase tracking-kicker text-body-soft">
        {label}
      </p>
      <p className="mt-1.5 nums font-display text-2xl font-extrabold leading-none tracking-tightest text-body">
        {hasValue ? fmtNum(value, 0) : "–"}
      </p>
      {rawCount ? null : (
        <p className="mt-1 text-xs font-bold text-body-faint">{unit}</p>
      )}
    </div>
  );
}

// --- section header ---------------------------------------------------------

function SectionHeader({
  title,
  subtitle,
  onAdd,
  addLabel,
}: {
  title: string;
  subtitle: string;
  onAdd: () => void;
  addLabel: string;
}) {
  return (
    <div className="mb-3 flex items-end justify-between gap-3">
      <div>
        <h2 className="font-display text-lg font-extrabold tracking-tightest text-body">
          {title}
        </h2>
        <p className="mt-0.5 text-sm text-body-soft">{subtitle}</p>
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={onAdd}
        iconLeft={<IconPlus width={16} height={16} />}
        className="hidden shrink-0 sm:inline-flex"
      >
        {addLabel}
      </Button>
    </div>
  );
}

// --- set card ---------------------------------------------------------------

function SetCard({ set, name, index }: { set: SetEntry; name: string; index: number }) {
  const { t } = useTranslation("sessions");
  const reduce = useReducedMotion();
  const volume = set.volume ?? setVolume(set);

  const metrics: Array<{ label: string; value: string }> = [];
  if (set.reps != null) metrics.push({ label: t("sets.reps"), value: fmtNum(set.reps) });
  if (set.weight_kg != null)
    metrics.push({ label: t("sets.weight"), value: `${fmtNum(set.weight_kg, 1)} ${t("sets.kg")}` });
  if (set.rpe != null) metrics.push({ label: t("sets.rpe"), value: fmtNum(set.rpe, 1) });
  if (set.rir != null) metrics.push({ label: t("sets.rir"), value: fmtNum(set.rir) });

  return (
    <motion.li
      initial={reduce ? false : { opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.25, delay: reduce ? 0 : Math.min(index * 0.03, 0.3) }}
      className="flex items-center gap-3 rounded-md bg-surface p-3 ring-1 ring-line"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-surface-sunken font-display text-sm font-extrabold text-body-soft">
        {set.set_no ?? index + 1}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-sm font-bold text-body">
          {name || <span className="text-body-faint">—</span>}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-body-soft">
          {metrics.map((m) => (
            <span key={m.label}>
              <span className="text-body-faint">{m.label} </span>
              <span className="font-bold text-body-muted">{m.value}</span>
            </span>
          ))}
          {set.unilateral_side && (
            <span className="capitalize text-body-muted">{set.unilateral_side}</span>
          )}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <p className="nums font-display text-sm font-extrabold text-body">
          {volume != null ? fmtNum(volume) : "–"}
        </p>
        <p className="text-[0.65rem] font-bold uppercase tracking-kicker text-body-faint">
          {t("sets.volume")}
        </p>
      </div>
    </motion.li>
  );
}

// --- cardio card ------------------------------------------------------------

function CardioCard({
  cardio,
  name,
  index,
}: {
  cardio: CardioEntry;
  name: string;
  index: number;
}) {
  const { t } = useTranslation("sessions");
  const reduce = useReducedMotion();

  const summary = cardioSummary(cardio, {
    km: t("cardio.distanceUnit"),
    min: t("cardio.timeUnit"),
    bpm: t("cardio.bpm"),
  });

  const intensityTone =
    cardio.intensity === "hard"
      ? "danger"
      : cardio.intensity === "moderate"
        ? "warning"
        : "success";

  return (
    <motion.li
      initial={reduce ? false : { opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.25, delay: reduce ? 0 : Math.min(index * 0.03, 0.3) }}
      className="flex items-center gap-3 rounded-md bg-surface p-3 ring-1 ring-line"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-grad-lime text-white">
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path d="M13 4.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM7 21l3-6 3 2 1 4M10 15l-1-4 4-2 3 3h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-sm font-bold text-body">
          {name || t("cardio.title")}
        </p>
        {summary && (
          <p className="mt-0.5 truncate text-xs text-body-soft">{summary}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {cardio.calories_override != null && (
          <span className="nums text-xs font-bold text-body-muted">
            {fmtNum(cardio.calories_override)} {t("log.calUnit")}
          </span>
        )}
        {cardio.intensity && (
          <Badge tone={intensityTone}>
            {t(
              cardio.intensity === "hard"
                ? "cardio.intensityHard"
                : cardio.intensity === "moderate"
                  ? "cardio.intensityModerate"
                  : "cardio.intensityEasy",
            )}
          </Badge>
        )}
      </div>
    </motion.li>
  );
}
